import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { Share2, ChevronLeft, Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { fetchPages } from "../../../../redux/blogSlice";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,600&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME  —  pure white · ink black · zero gold
───────────────────────────────────────────────────────── */
const C = {
  paper:   "#ffffff",
  surface: "#f7f7f7",
  ink:     "#0a0a0a",
  inkSoft: "#2a2a2a",
  inkMid:  "#6b6b6b",
  inkLight:"#a3a3a3",
  border:  "#e0e0e0",
  serif:   "'Cormorant Garamond', Georgia, serif",
  sans:    "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   RICH TEXT RENDERER
   Supports: **bold**, _italic_, [label](url), bare https://,
   email@domain, ## headings, - bullets, blank-line spacing,
   HTML passthrough
───────────────────────────────────────────────────────── */
const renderRichLine = (text: string, key: number | string): React.ReactNode => {
  const regex = /(\*\*(.+?)\*\*|_(.+?)_|\[(.+?)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}))/g;
  const parts: React.ReactNode[] = [];
  let last = 0; let m: RegExpExecArray | null; let k = 0;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > last)
      parts.push(<span key={k++}>{text.slice(last, m.index)}</span>);

    if (m[2])       parts.push(<strong key={k++} style={{ fontWeight:600, color:C.ink }}>{m[2]}</strong>);
    else if (m[3])  parts.push(<em key={k++} style={{ fontStyle:"italic" }}>{m[3]}</em>);
    else if (m[4])  parts.push(<a key={k++} href={m[5]} target="_blank" rel="noopener noreferrer" style={linkStyle}>{m[4]}</a>);
    else if (m[6])  parts.push(<a key={k++} href={m[6]} target="_blank" rel="noopener noreferrer" style={{ ...linkStyle, fontSize:12, wordBreak:"break-all" }}>{m[6]}</a>);
    else if (m[7])  parts.push(<a key={k++} href={`mailto:${m[7]}`} style={linkStyle}>{m[7]}</a>);

    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={k++}>{text.slice(last)}</span>);
  return <React.Fragment key={key}>{parts.length ? parts : text}</React.Fragment>;
};

const linkStyle: React.CSSProperties = {
  color: C.ink, fontWeight: 500,
  textDecoration: "underline", textUnderlineOffset: 3,
  textDecorationColor: "#cacaca",
};

const renderRichContent = (raw: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const lines = raw.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // blank line
    if (!trimmed) { nodes.push(<div key={`sp${i}`} style={{ height:12 }}/>); i++; continue; }

    // h1
    if (/^#\s/.test(line)) {
      nodes.push(
        <h2 key={`h1${i}`} style={{ fontFamily:C.serif, fontSize:"clamp(28px,4vw,40px)", fontWeight:600, color:C.ink, lineHeight:1.15, margin:"32px 0 12px", letterSpacing:"-0.01em" }}>
          {renderRichLine(line.replace(/^#+\s/,""), `h1t${i}`)}
        </h2>
      );
      i++; continue;
    }

    // h2
    if (/^#{2}\s/.test(line)) {
      nodes.push(
        <h3 key={`h2${i}`} style={{ fontFamily:C.serif, fontSize:"clamp(20px,3vw,28px)", fontWeight:600, color:C.ink, lineHeight:1.2, margin:"28px 0 10px" }}>
          {renderRichLine(line.replace(/^#+\s/,""), `h2t${i}`)}
        </h3>
      );
      i++; continue;
    }

    // h3
    if (/^#{3}\s/.test(line)) {
      nodes.push(
        <h4 key={`h3${i}`} style={{ fontFamily:C.sans, fontSize:13, fontWeight:700, color:C.ink, letterSpacing:"0.14em", textTransform:"uppercase", margin:"24px 0 8px" }}>
          {renderRichLine(line.replace(/^#+\s/,""), `h3t${i}`)}
        </h4>
      );
      i++; continue;
    }

    // bullet list — collect consecutive bullets
    if (/^[-*•]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*•]\s/.test(lines[i].trim())) {
        items.push(lines[i].replace(/^[-*•]\s/,""));
        i++;
      }
      nodes.push(
        <ul key={`ul${i}`} style={{ margin:"12px 0 16px", paddingLeft:0, listStyle:"none", display:"flex", flexDirection:"column", gap:8 }}>
          {items.map((item, idx) => (
            <li key={idx} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
              <span style={{ width:4, height:4, borderRadius:"50%", background:C.inkMid, marginTop:9, flexShrink:0 }}/>
              <span style={{ fontSize:15, lineHeight:1.75, color:C.inkSoft, fontFamily:C.sans, fontWeight:300 }}>
                {renderRichLine(item, `li${i}-${idx}`)}
              </span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // horizontal rule
    if (/^---+$/.test(trimmed)) {
      nodes.push(<div key={`hr${i}`} style={{ height:1, background:C.border, margin:"28px 0" }}/>);
      i++; continue;
    }

    // blockquote
    if (/^>\s/.test(line)) {
      nodes.push(
        <blockquote key={`bq${i}`} style={{
          borderLeft:`2px solid ${C.ink}`, margin:"20px 0",
          paddingLeft:20, paddingTop:4, paddingBottom:4,
        }}>
          <p style={{ fontFamily:C.serif, fontSize:18, fontStyle:"italic", color:C.inkSoft, lineHeight:1.7, margin:0 }}>
            {renderRichLine(line.replace(/^>\s/,""), `bqt${i}`)}
          </p>
        </blockquote>
      );
      i++; continue;
    }

    // paragraph — collect consecutive lines as one paragraph
    const paraLines: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^[#>\-*•]/.test(lines[i]) && !/^---+$/.test(lines[i].trim())) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      nodes.push(
        <p key={`p${i}`} style={{ fontSize:15, lineHeight:1.85, color:C.inkSoft, fontFamily:C.sans, fontWeight:300, margin:"0 0 14px" }}>
          {paraLines.map((pl, pi) => (
            <React.Fragment key={pi}>
              {renderRichLine(pl, `pl${i}-${pi}`)}
              {pi < paraLines.length - 1 && " "}
            </React.Fragment>
          ))}
        </p>
      );
    }
  }
  return nodes;
};

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const EPageContent: React.FC = () => {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const dispatch   = useDispatch<AppDispatch>();
  const { pages }  = useSelector((state: RootState) => state.blog);
  const page       = pages.find((p) => p.slug === slug);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    if (!pages.length) dispatch(fetchPages());
  }, [dispatch, pages.length]);

  const handleShare = () => {
    if (!page) return;
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: page.title, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  /* Loading state */
  if (!page) {
    return (
      <div style={{ minHeight:"100vh", background:C.paper, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <style>{FONT_CSS}</style>
        <div style={{ textAlign:"center" }}>
          <div style={{ width:32, height:1, background:C.border, margin:"0 auto 20px" }}/>
          <p style={{ fontSize:12, color:C.inkLight, letterSpacing:"0.2em", fontFamily:C.sans }}>LOADING</p>
        </div>
      </div>
    );
  }

  const isHtmlContent = /<\/?[a-z][\s\S]*>/i.test(page.content);

  /* Word count for reading time */
  const wordCount   = page.content.trim().split(/\s+/).length;
  const readingTime = Math.max(1, Math.round(wordCount / 220));

  return (
    <div style={{ minHeight:"100vh", background:C.paper, color:C.ink, fontFamily:C.sans }}>
      <style>{FONT_CSS + PAGE_CSS}</style>

      {/* ════════════════════════════════
          TOP NAV BAR
      ════════════════════════════════ */}
      <header style={S.topbar}>
        {/* Left: back */}
        <button onClick={() => navigate(-1)} style={S.backBtn}>
          <ChevronLeft size={15} strokeWidth={1.5}/>
          <span style={S.backLabel}>Back</span>
        </button>

        {/* Center: wordmark */}
        <div style={S.wordmark}>
          <span style={S.wordmarkMain}>4REVA GROUP</span>
        </div>

        {/* Right: share */}
        <button onClick={handleShare} style={S.shareBtn} title="Share">
          {copied
            ? <><Check size={14}/><span style={S.shareLabel}>Copied</span></>
            : <><Share2 size={14}/><span style={S.shareLabel}>Share</span></>
          }
        </button>
      </header>

      {/* ════════════════════════════════
          ARTICLE HEADER
      ════════════════════════════════ */}
      <div style={S.articleHead}>
        {/* Top rule */}
        <div style={{ height:2, background:C.ink, marginBottom:32 }}/>

        {/* Category / meta row */}
        <motion.div
          initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.5 }}
          style={S.metaRow}
        >
          <span style={S.metaTag}>4REVA GROUP</span>
          <span style={S.metaDot}>·</span>
          <span style={S.metaItem}>{readingTime} min read</span>
          <span style={S.metaDot}>·</span>
          <span style={S.metaItem}>{wordCount.toLocaleString()} words</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.6, delay:0.1 }}
          style={{
            ...S.articleTitle,
            fontSize: isMobile ? "clamp(32px, 9vw, 48px)" : "clamp(40px, 5vw, 72px)",
          }}
        >
          {page.title}
        </motion.h1>

        {/* Bottom rule */}
        <motion.div
          initial={{ scaleX:0 }} animate={{ scaleX:1 }}
          transition={{ duration:0.8, delay:0.3, ease:[0.4,0,0.2,1] }}
          style={{ height:1, background:C.border, marginTop:32, transformOrigin:"left" }}
        />
      </div>

      {/* ════════════════════════════════
          CONTENT
      ════════════════════════════════ */}
      <motion.div
        initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
        transition={{ duration:0.6, delay:0.35 }}
        style={{
          ...S.contentWrap,
          columnCount: isMobile ? 1 : (page.content.length > 1200 ? 2 : 1),
          columnGap: "56px",
          columnRule: `1px solid ${C.border}`,
        }}
      >
        {isHtmlContent ? (
          /* HTML passthrough — sanitised display */
          <div
            className="ep-html"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        ) : (
          /* Rich markdown-lite renderer */
          renderRichContent(page.content)
        )}
      </motion.div>

      {/* ════════════════════════════════
          SHARE FOOTER STRIP
      ════════════════════════════════ */}
      <div style={S.shareStrip}>
        <div style={{ height:1, background:C.border, marginBottom:28 }}/>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
          <div>
            <p style={S.shareStripLabel}>SHARE THIS PAGE</p>
            <p style={{ fontSize:12, color:C.inkLight, marginTop:4, fontFamily:C.sans }}>{page.title}</p>
          </div>
          <button onClick={handleShare} style={S.shareStripBtn}>
            {copied
              ? <><Check size={14} style={{ marginRight:6 }}/> LINK COPIED</>
              : <><Share2 size={14} style={{ marginRight:6 }}/> COPY LINK</>
            }
          </button>
        </div>
      </div>

      {/* ════════════════════════════════
          FOOTER
      ════════════════════════════════ */}
      <div style={{ marginTop:80 }}>
      </div>
    </div>
  );
};

export default EPageContent;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {

  /* Top bar */
  topbar: {
    position: "sticky", top:0, zIndex:100,
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(14px)",
    borderBottom: `1px solid ${C.border}`,
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    padding: "0 clamp(16px,4vw,40px)", height: 58,
  },
  backBtn: {
    display: "flex", alignItems: "center", gap:5,
    background: "none", border: "none", cursor:"pointer",
    color: C.inkMid, fontFamily: C.sans,
    fontSize:11, letterSpacing:"0.1em", fontWeight:500,
    padding:0, flexShrink:0,
  },
  backLabel: { display:"inline" },
  wordmark: {
    position:"absolute", left:"50%", transform:"translateX(-50%)",
    pointerEvents:"none",
  },
  wordmarkMain: {
    fontFamily: C.serif, fontSize:16, fontWeight:600,
    letterSpacing:"0.28em", color:C.ink,
  },
  shareBtn: {
    display: "flex", alignItems:"center", gap:5,
    background: "none", border:`1px solid ${C.border}`,
    cursor:"pointer", color:C.inkMid,
    fontSize:10, letterSpacing:"0.12em", fontWeight:500,
    padding:"7px 12px", fontFamily:C.sans, flexShrink:0,
    transition:"border-color 0.2s, color 0.2s",
  },
  shareLabel: { display:"inline" },

  /* Article header */
  articleHead: {
    maxWidth: 880, margin:"0 auto",
    padding: "40px clamp(20px,5vw,48px) 0",
  },
  metaRow: {
    display: "flex", alignItems:"center", gap:10,
    marginBottom:20, flexWrap:"wrap",
  },
  metaTag: {
    fontSize:9, fontWeight:700, letterSpacing:"0.28em",
    color:C.ink, fontFamily:C.sans,
    border:`1px solid ${C.ink}`, padding:"3px 10px",
  },
  metaDot:  { fontSize:10, color:C.border },
  metaItem: { fontSize:10, color:C.inkLight, letterSpacing:"0.1em", fontFamily:C.sans },
  articleTitle: {
    fontFamily: C.serif, fontWeight:600, color:C.ink,
    lineHeight:1.06, margin:0, letterSpacing:"-0.015em",
  },

  /* Content */
  contentWrap: {
    maxWidth: 880, margin: "0 auto",
    padding: "48px clamp(20px,5vw,48px) 0",
  },

  /* Share strip */
  shareStrip: {
    maxWidth: 880, margin: "60px auto 0",
    padding: "0 clamp(20px,5vw,48px)",
  },
  shareStripLabel: {
    fontSize:9, letterSpacing:"0.3em", color:C.ink,
    fontWeight:700, fontFamily:C.sans,
  },
  shareStripBtn: {
    display: "inline-flex", alignItems:"center",
    background: C.ink, color:"#fff", border:"none",
    padding:"12px 24px", fontSize:10, fontWeight:700,
    letterSpacing:"0.2em", cursor:"pointer",
    fontFamily:C.sans, flexShrink:0,
  },
};

/* ─────────────────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────────────────── */
const PAGE_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }

  /* HTML passthrough base styles */
  .ep-html p   { font-size:15px; line-height:1.85; color:#2a2a2a; font-family:'Montserrat',sans-serif; font-weight:300; margin-bottom:14px; }
  .ep-html h1,
  .ep-html h2  { font-family:'Cormorant Garamond',serif; color:#0a0a0a; font-weight:600; margin:28px 0 12px; line-height:1.15; }
  .ep-html h1  { font-size:clamp(26px,3.5vw,38px); }
  .ep-html h2  { font-size:clamp(20px,2.8vw,28px); }
  .ep-html h3  { font-family:'Montserrat',sans-serif; font-size:11px; font-weight:700; letter-spacing:0.2em; text-transform:uppercase; color:#0a0a0a; margin:22px 0 8px; }
  .ep-html a   { color:#0a0a0a; font-weight:500; text-underline-offset:3px; }
  .ep-html ul  { padding-left:0; list-style:none; margin:12px 0 16px; }
  .ep-html li  { display:flex; gap:12px; align-items:flex-start; margin-bottom:8px; font-size:15px; line-height:1.75; color:#2a2a2a; font-family:'Montserrat',sans-serif; font-weight:300; }
  .ep-html li::before { content:''; width:4px; height:4px; border-radius:50%; background:#6b6b6b; margin-top:9px; flex-shrink:0; }
  .ep-html blockquote { border-left:2px solid #0a0a0a; padding:4px 0 4px 20px; margin:20px 0; font-style:italic; font-family:'Cormorant Garamond',serif; font-size:18px; color:#2a2a2a; }
  .ep-html hr  { height:1px; background:#e0e0e0; border:none; margin:28px 0; }
  .ep-html img { max-width:100%; height:auto; display:block; margin:20px 0; }
  .ep-html strong { font-weight:600; color:#0a0a0a; }

  @media (max-width: 640px) {
    .ep-back-label, .ep-share-label { display: none; }
  }
`;